import React, { useCallback, useRef, useState } from 'react'
import { Box, Button, TextField, Typography } from '@mui/material'
import { makeStyles } from 'tss-react/mui'
import { useTranslation } from 'gatsby-plugin-react-i18next'
import ComplianceNote from '../../ComplianceNote/ComplianceNote'
import EmailSentIcon from '../../../images/emailsent.inline.svg'
import { Endpoints } from '../../../types/constants'
import { PartnerApiResponse, WidgetTRefs } from '../../../types/widget'
import useAxios from '../../../hooks/useAxios'
import { useComplianceNote } from '../../../hooks/useComplianceNote'
import useSiteMetadata from '../../../hooks/useSiteMetadata'
import { EventNames, useStatsEvent } from '../../../hooks/useStatsEvent'
import useUser from '../../../hooks/useUser'
import { BindToFields, CssEntry } from '../../../types/contentfulAttributes'
import { MixpanelCTALinkActions, MixpanelEvents } from '../../../types/mixpanel'
import { createContentfulClassGetter } from '../../../utils/contentful'
import {
  useMixpanelCTAClickTracking,
  useMpEventType,
} from '../../../utils/mixpanel'
import { titleCased } from '../../../utils/string'
import { isEmail } from '../../../utils/validation'

interface MobileEmailSubmitProps {
  css: CssEntry[] | null
}

export default function MobileEmailSubmit({
  css,
}: MobileEmailSubmitProps): JSX.Element {
  const { classes, cx } = useStyles()
  const { t } = useTranslation()
  const [apiStatus, setApiStatus] = useState<'idle' | 'loading' | 'success'>(
    'idle'
  )
  const [error, setError] = useState({ email: '', runtime: '' })
  const successEmailRef = useRef<string>('')
  const axios = useAxios()
  const { handleComplianceModalOpen } = useComplianceNote()
  const { pt } = useUser()
  const { defaultResumeProduct } = useSiteMetadata()
  const { mutate } = useStatsEvent()
  const handleMpCTAClickTracking = useMixpanelCTAClickTracking()
  const submitBtnRef = useRef<HTMLButtonElement | null>(null)
  const mpEventType = useMpEventType()
  const applyContentfulStyles = createContentfulClassGetter(css)

  const resetErrorState = useCallback(() => {
    if (error.email || error.runtime) setError({ email: '', runtime: '' })
  }, [error.email, error.runtime])

  const handleSubmit = useCallback(
    (e: React.FormEvent<HTMLFormElement>) => {
      e.preventDefault()
      if (['loading', 'success'].includes(apiStatus)) return
      const data = Object.fromEntries(
        new FormData(e.currentTarget).entries()
      ) as { email: string }
      if (!isEmail(data.email)) {
        return setError((val) => ({
          ...val,
          email: t(`${WidgetTRefs.CommonTRef}.emailError`),
        }))
      }

      if (submitBtnRef.current?.textContent) {
        handleMpCTAClickTracking(
          submitBtnRef.current.textContent,
          MixpanelCTALinkActions.EmailSubmitAction
        )
      }

      handleMpCTAClickTracking(null, MixpanelCTALinkActions.EmailSubmitAction, {
        eventName: MixpanelEvents.EmailAddressAddedToResumeUpload,
        trackingData: { event_type: mpEventType },
      })

      resetErrorState()
      setApiStatus('loading')
      axios
        .post<PartnerApiResponse>(Endpoints.LeadsEmail, data, {
          params: { partner_key: atob(pt ?? '') },
          validateStatus: (status) => status === 200 || status === 409,
        })
        .then((response) => {
          const {
            status: { code: statusCode },
          } = response.data
          if (statusCode === '200') {
            mutate(EventNames.frontendMobileEmailUpload)
          } else {
            mutate(EventNames.frontendMobileEmailUploadFailure)
          }
          successEmailRef.current = data.email
          setApiStatus('success')
        })
        .catch(() => {
          mutate(EventNames.frontendMobileEmailUploadFailure)
          setApiStatus('idle')
          setError((val) => ({
            ...val,
            runtime: t(`${WidgetTRefs.MobileTRef}.runtimeError`),
          }))
        })
    },
    [
      apiStatus,
      handleMpCTAClickTracking,
      mpEventType,
      resetErrorState,
      axios,
      pt,
      t,
      mutate,
    ]
  )
  return (
    <Box className={classes.box}>
      {apiStatus === 'success' ? (
        <Box>
          <EmailSentIcon />
          <Typography variant="h2" className={classes.successText}>
            {t(`${WidgetTRefs.MobileTRef}.successText1`)}
          </Typography>
          <Box className={classes.successText}>
            <Typography variant="body2" className={classes.sentEmailText}>
              {t(`${WidgetTRefs.MobileTRef}.successText2`, {
                Product: titleCased(defaultResumeProduct),
              })}
              &nbsp;
              <strong className={classes.email}>
                {successEmailRef.current}
              </strong>
            </Typography>
            <Typography variant="body2">
              {t(`${WidgetTRefs.MobileTRef}.successText3`, {
                defaultResumeProduct,
              })}
            </Typography>
            <br />
            <Typography variant="body2">
              <strong>
                {t(`${WidgetTRefs.MobileTRef}.successText4`, {
                  defaultResumeProduct,
                })}
              </strong>
            </Typography>
          </Box>
        </Box>
      ) : (
        <>
          <Box
            component="form"
            onSubmit={handleSubmit}
            className={cx(classes.form, apiStatus === 'loading' && classes.dim)}
          >
            <TextField
              disabled={apiStatus === 'loading'}
              fullWidth
              color="primary"
              onChange={resetErrorState}
              margin="normal"
              required
              placeholder={t(`${WidgetTRefs.MobileTRef}.emailPlaceholder`)}
              name="email"
              autoFocus
              variant="outlined"
              error={!!error.email}
              helperText={error.email || error.runtime}
              FormHelperTextProps={{ className: classes.helperText }}
              InputProps={{ className: classes.emailInput }}
            />
            <Button
              fullWidth
              type="submit"
              disabled={apiStatus === 'loading' || !!error.email}
              ref={submitBtnRef}
            >
              <Typography>
                {t(`${WidgetTRefs.MobileTRef}.resumeLinkText`, {
                  defaultResumeProduct,
                })}
              </Typography>
            </Button>
          </Box>
          <ComplianceNote>
            <Box
              className={cx(
                classes.privacyText,
                applyContentfulStyles(BindToFields.RichTextH1)
              )}
            >
              <Typography component="span" variant="caption">
                {t(`${WidgetTRefs.MobileTRef}.privacyText`)}&nbsp;
              </Typography>

              <ComplianceNote.TextLink onClick={handleComplianceModalOpen}>
                {t(`${WidgetTRefs.CommonTRef}.tos`)}
              </ComplianceNote.TextLink>

              <Typography component="span" variant="caption">
                &nbsp;and&nbsp;
              </Typography>

              <ComplianceNote.TextLink onClick={handleComplianceModalOpen}>
                {t(`${WidgetTRefs.CommonTRef}.pp`)}
              </ComplianceNote.TextLink>

              <Typography component="span" variant="caption">
                .
              </Typography>
            </Box>
          </ComplianceNote>
        </>
      )}
      <br />
    </Box>
  )
}

const useStyles = makeStyles()((theme) => ({
  box: {
    display: 'flex',
    flexDirection: 'column',
    maxWidth: theme.breakpoints.values.mobile,
    margin: 'auto',
    paddingBlock: theme.spacing(2),
    textAlign: 'center',
  },
  emailInput: {
    '& input': {
      textAlign: 'center',
    },
    '&:hover': {
      outline: `2px solid ${theme.colors.blue['400']}`,
      '& fieldset': {
        border: 'none',
      },
    },
  },
  successText: {
    color: '#54575c',
    textAlign: 'center',
  },
  form: {
    backgroundColor: theme.palette.background.default,
    paddingInline: theme.spacing(2),
    paddingBottom: theme.spacing(2),
    borderRadius: theme.spacing(0.75),
  },
  helperText: {
    textAlign: 'center',
    color: `${theme.colors.red['300']}`,
  },
  dim: {
    opacity: 0.6,
  },
  sentEmailText: {
    paddingBlock: theme.spacing(2),
    marginBottom: theme.spacing(3),
    wordBreak: 'break-word',
    padding: '0.5rem',
    color: '#54575c',
  },
  email: {
    color: '#1a91f0',
  },
  privacyText: {
    textAlign: 'center',
    marginTop: '1rem',
    '&&': {
      color: theme.palette.text.primary,
    },
  },
}))
